import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const CATEGORY_LABEL = {
  grant: "Granted",
  split: "Split",
  nullifier: "Nullified (internal)",
  order_debit: "Spent",
  refund: "Refund",
  debit: "Debit",
};

function el(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

// Builds the lineage tree as real DOM nodes rather than an HTML string. Every value below comes
// from the server's JSON response, so keeping it on textContent (rather than an escaped-then-
// interpolated string handed to innerHTML) means there is no per-call escaping step to remember
// or to accidentally skip on a future edit.
function renderNode(node, focusClass) {
  if (!node) {
    return null;
  }

  const typeClass = node.is_credit ? "credit" : "debit";
  const label = CATEGORY_LABEL[node.category] || (node.is_credit ? "Credit" : "Debit");

  let target = "";
  if (node.category === "order_debit" || node.category === "refund") {
    target = `Order #${node.accountable_id}`;
  } else if (node.category === "grant" && node.accountable_type) {
    target = `from ${node.accountable_type} #${node.accountable_id}`;
  }

  const supporting = node.notes || (node.reason && node.reason !== node.event_type ? node.reason : "");

  const li = el("li", `lineage-node ${typeClass}`);
  if (node.is_focus) {
    li.classList.add(focusClass);
  }

  const row = el("div", "lineage-row");
  row.appendChild(el("span", "lineage-badge", label));
  row.appendChild(el("strong", "lineage-amount", node.amount));
  row.appendChild(el("span", "lineage-id", `#${node.id}`));
  if (target) {
    row.appendChild(el("span", "lineage-meta", target));
  }
  if (node.deleted_at) {
    row.appendChild(el("span", "lineage-deleted", "closed"));
  }
  if (supporting) {
    row.appendChild(el("em", "lineage-reason", supporting));
  }
  li.appendChild(row);

  if (node.children && node.children.length > 0) {
    const childList = document.createElement("ul");
    node.children.forEach(child => {
      const childNode = renderNode(child, focusClass);
      if (childNode) {
        childList.appendChild(childNode);
      }
    });
    li.appendChild(childList);
  }

  return li;
}

// Searches for an account action by id, walks up to its original credit on the server, and
// renders the full descendant tree client-side from the JSON the server returns. On the account
// action Show page there is no search box at all - `prefillAccountActionIdValue` stands in for
// the input and the tree loads on connect.
export default class extends Controller {
  static get targets() {
    return ["input", "error", "treeContainer", "tree"];
  }

  static get values() {
    return { organizationId: Number, prefillAccountActionId: String };
  }

  // "lineage-focus" (unlike "hidden" -- see the note in showTree) is used nowhere in the app
  // outside this controller's own partial, so it's genuinely controller-owned; see
  // CONVENTIONS.md, "Why isn't this hardcoded class on the Classes API?". No view declares an
  // override, so the read stays
  // guarded rather than bare -- see pitfalls.md P33.
  static get classes() {
    return ["focus"];
  }

  connect() {
    if (this.hasPrefillAccountActionIdValue && this.prefillAccountActionIdValue !== "") {
      this.loadLineage();
    }
  }

  loadLineage() {
    const accountActionId = this.hasInputTarget ? this.inputTarget.value : this.prefillAccountActionIdValue;

    if (!accountActionId) {
      this.showError("Please enter an account action ID");
      return;
    }

    this.hideError();
    this.hideTree();

    const url = new URL(
      `/admin/organizations/${this.organizationIdValue}/account_actions/diagnostics/lineage`,
      window.location.origin
    );
    url.searchParams.set("account_action_id", accountActionId);

    fetch(url, RequestHelper.defaultRequestOptions()).
      then(response => {
        return response.json().
          then(data => ({ ok: response.ok, data })).
          catch(() => ({ ok: response.ok, data: null }));
      }).
      then(({ ok, data }) => {
        if (ok) {
          this.showTree(data);
        } else {
          this.showError((data && data.error) || "Failed to load lineage");
        }
      }).
      catch(() => this.showError("Failed to load lineage"));
  }

  showTree(node) {
    const focusClass = this.hasFocusClass ? this.focusClass : "lineage-focus";

    const root = document.createElement("ul");
    root.className = "lineage-tree-root";
    const rootNode = renderNode(node, focusClass);
    if (rootNode) {
      root.appendChild(rootNode);
    }

    this.treeTarget.textContent = "";
    this.treeTarget.appendChild(root);
    // "hidden" is Bootstrap's own global utility class (bootstrap-yeti.scss), not a name this
    // controller owns -- see CONVENTIONS.md. Left as a literal.
    this.treeContainerTarget.classList.remove("hidden");
  }

  hideTree() {
    this.treeTarget.textContent = "";
    this.treeContainerTarget.classList.add("hidden");
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  hideError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }
}
